import React, { useEffect, useRef, useState } from 'react';
import { insertSkillScore, CATEGORY_OPTIONS } from './skillData';
import { colors, radius, fontStack } from '../design-system/tokens';

/**
 * CVUploadModal — Phase 10.
 *
 * State machine: idle → parsing → review → saving → error
 * (saving collapses back to idle/closed on success; error has a "Try
 * again" path that returns to idle.)
 *
 * pdfjs-dist is imported dynamically so the ~1MB API surface only
 * loads when the modal is opened. The worker is self-hosted at
 * /pdf.worker.min.mjs (copied from node_modules at install time).
 *
 * Props:
 *   - open: boolean
 *   - onClose(): close without saving
 *   - userId: string  — current user id from useGrowthData
 *   - onSaved({ count }): called after a successful bulk insert.
 *     Parent should refresh data + show its own toast/banner.
 */

const FILE_LIMITS = {
  maxBytes: 5 * 1024 * 1024, // 5 MB
  minTextChars: 200,
  maxTextChars: 15000,
};

const PROFILE_KEY = 'curx_user_profile';

export default function CVUploadModal({ open, onClose, userId, onSaved }) {
  const [phase, setPhase] = useState('idle'); // idle | parsing | review | saving | error
  const [progress, setProgress] = useState(0); // 0..2 (status line index during parsing)
  const [errorTitle, setErrorTitle] = useState('Something went wrong');
  const [errorMessage, setErrorMessage] = useState('');
  const [extracted, setExtracted] = useState(null); // { skills[], experience_level, primary_domain }
  const [editedSkills, setEditedSkills] = useState([]); // [{ id, skill_name, category, score, dirty }]
  const [editingId, setEditingId] = useState(null);
  const [saveError, setSaveError] = useState('');
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragHover, setDragHover] = useState(false);

  // Reset whenever the modal opens fresh.
  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setProgress(0);
    setErrorTitle('Something went wrong');
    setErrorMessage('');
    setExtracted(null);
    setEditedSkills([]);
    setEditingId(null);
    setSaveError('');
  }, [open]);

  // Esc to close (blocked while a network call is in flight).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && phase !== 'parsing' && phase !== 'saving') {
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase, onClose]);

  if (!open) return null;

  /* ================================================================
     File handling + parsing pipeline
     ================================================================ */

  const showError = (title, message) => {
    setErrorTitle(title);
    setErrorMessage(message);
    setPhase('error');
  };

  const validateFile = (file) => {
    if (!file) return 'No file selected.';
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) return 'PDF only please.';
    if (file.size > FILE_LIMITS.maxBytes) return 'File too large (max 5MB).';
    return null;
  };

  const handleFile = async (file) => {
    const err = validateFile(file);
    if (err) {
      showError("Couldn't read this PDF", err);
      return;
    }

    setPhase('parsing');
    setProgress(0);

    // 30-second client timeout (server's is 25, leaves 5s margin).
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    try {
      // Step 1: extract text from PDF.
      setProgress(0);
      const text = await extractPdfText(file);
      if (text.trim().length < FILE_LIMITS.minTextChars) {
        throw new Error('PDF appears empty or unreadable. Try exporting an unprotected version.');
      }

      // Step 2: hand off to Sonnet.
      setProgress(1);
      const res = await fetch('/api/parse-cv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_text: text.slice(0, FILE_LIMITS.maxTextChars) }),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || `Server returned ${res.status}`);
      }

      setProgress(2);
      const skills = Array.isArray(data?.skills) ? data.skills : [];
      if (skills.length === 0) {
        showError(
          "We didn't find any skills",
          "The CV came through but Sonnet couldn't pull out any skill signals. Try a different PDF, or add skills manually instead."
        );
        return;
      }

      setExtracted({
        skills,
        experience_level: data.experience_level || 'junior',
        primary_domain: data.primary_domain || 'Other',
      });
      setEditedSkills(
        skills.map((s, i) => ({
          id: `cv-${i}`,
          skill_name: s.skill_name,
          category: s.category,
          score: s.score,
          dirty: false,
        }))
      );
      setPhase('review');
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      if (e?.name === 'AbortError') {
        showError("This took too long", "Try a shorter CV or check your connection, then try again.");
      } else if (msg.includes('password') || msg.includes('encrypted')) {
        showError(
          "Can't read encrypted PDFs",
          'Try exporting an unprotected version of your CV and upload that instead.'
        );
      } else if (msg.includes('appears empty') || msg.includes('too short')) {
        showError("PDF appears empty", e.message);
      } else {
        showError("Something went wrong", e?.message || 'Could not parse this CV.');
      }
    } finally {
      clearTimeout(timeoutId);
      abortRef.current = null;
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (file) handleFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!draggingRef.current) {
      draggingRef.current = true;
      setDragHover(true);
    }
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    draggingRef.current = false;
    setDragHover(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    draggingRef.current = false;
    setDragHover(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const cancelParsing = () => {
    abortRef.current?.abort();
    setPhase('idle');
  };

  /* ================================================================
     Review state — edit, remove, add
     ================================================================ */

  const updateRow = (id, patch) => {
    setEditedSkills((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch, dirty: true } : r))
    );
  };
  const removeRow = (id) => {
    setEditedSkills((rows) => rows.filter((r) => r.id !== id));
    if (editingId === id) setEditingId(null);
  };
  const addRow = () => {
    const id = `new-${Date.now()}`;
    setEditedSkills((rows) => [
      ...rows,
      { id, skill_name: '', category: 'technical', score: 70, dirty: true },
    ]);
    setEditingId(id);
  };

  const validRows = editedSkills.filter(
    (r) =>
      String(r.skill_name || '').trim().length > 0 &&
      Number.isFinite(Number(r.score)) &&
      Number(r.score) >= 0 &&
      Number(r.score) <= 100
  );
  const allRowsValid = validRows.length === editedSkills.length;

  /* ================================================================
     Save — bulk insert with source='cv-extracted'
     ================================================================ */

  const handleSave = async () => {
    if (!userId) {
      setSaveError('You need to be signed in to save these.');
      return;
    }
    if (!allRowsValid) {
      setSaveError('Some rows are still invalid. Fix or remove them.');
      return;
    }
    if (validRows.length === 0) {
      setSaveError('Nothing to save.');
      return;
    }

    setPhase('saving');
    setSaveError('');

    try {
      // Sequential inserts. Bulk parallel insert risks partial failure with
      // confusing error reporting; sequential keeps each error attributable.
      //
      // Phase 10 hotfix: the `skill_scores_source_check` DB constraint only
      // allows 'self-rated' today, so CV-extracted rows use that source for
      // now. A future migration will add 'cv-extracted' as a valid value
      // and we can flip this back.
      for (const r of validRows) {
        await insertSkillScore({
          user_id: userId,
          skill_name: String(r.skill_name).trim(),
          category: r.category,
          score: Math.round(Number(r.score)),
          source: 'self-rated',
        });
      }

      // Persist experience level + domain to localStorage for now (V2 will
      // migrate to a real user_profile table).
      try {
        if (extracted) {
          const payload = {
            experience_level: extracted.experience_level,
            primary_domain: extracted.primary_domain,
            updated_at: new Date().toISOString(),
          };
          window.localStorage.setItem(PROFILE_KEY, JSON.stringify(payload));
        }
      } catch (_) { /* private mode etc. */ }

      onSaved && (await onSaved({ count: validRows.length }));
      onClose && onClose();
    } catch (e) {
      setSaveError(e?.message || 'Could not save. Try again.');
      setPhase('review');
    }
  };

  /* ================================================================
     Render
     ================================================================ */

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Upload your CV"
      onClick={() => {
        if (phase !== 'parsing' && phase !== 'saving') onClose && onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 200,
        fontFamily: fontStack,
      }}
    >
      <div
        className="cv-upload-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 620,
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: radius.lg,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.textPrimary }}>
            {phase === 'review' ? 'Review extracted skills'
              : phase === 'parsing' ? 'Reading your CV…'
              : phase === 'saving' ? 'Saving…'
              : phase === 'error' ? errorTitle
              : 'Upload your CV'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={phase === 'parsing' || phase === 'saving'}
            aria-label="Close"
            style={closeBtn}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {phase === 'idle' && (
            <IdleView
              dragHover={dragHover}
              onPick={() => fileInputRef.current?.click()}
              onPickFile={onPickFile}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              fileInputRef={fileInputRef}
            />
          )}
          {phase === 'parsing' && <ParsingView progress={progress} />}
          {phase === 'review' && extracted && (
            <ReviewView
              extracted={extracted}
              skills={editedSkills}
              editingId={editingId}
              setEditingId={setEditingId}
              updateRow={updateRow}
              removeRow={removeRow}
              addRow={addRow}
              saveError={saveError}
            />
          )}
          {phase === 'saving' && (
            <div style={{ textAlign: 'center', padding: 24, color: colors.textSecondary }}>
              Saving {validRows.length} skill{validRows.length === 1 ? '' : 's'}…
            </div>
          )}
          {phase === 'error' && (
            <ErrorView
              title={errorTitle}
              message={errorMessage}
              onRetry={() => setPhase('idle')}
              onAddManually={() => { onClose && onClose(); /* parent should open ManageSkillsModal in response */ }}
            />
          )}
        </div>

        {/* Footer */}
        {(phase === 'idle' || phase === 'review' || phase === 'parsing') && (
          <div
            style={{
              padding: '14px 24px',
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {phase === 'parsing' ? (
              <button type="button" onClick={cancelParsing} style={ghostBtn}>Cancel</button>
            ) : phase === 'review' ? (
              <>
                <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!allRowsValid || validRows.length === 0}
                  style={primaryBtn(!allRowsValid || validRows.length === 0)}
                >
                  Save {validRows.length} skill{validRows.length === 1 ? '' : 's'}
                </button>
              </>
            ) : (
              <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .cv-upload-card {
            max-width: 100% !important;
            max-height: 100vh !important;
            height: 100vh;
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ================================================================
   pdfjs-dist text extraction (dynamic import)
   ================================================================ */

let pdfjsCache = null;
async function loadPdfJs() {
  if (pdfjsCache) return pdfjsCache;
  // Dynamic import so the ~1MB pdfjs API only ships when the modal is
  // first opened. Worker is self-hosted at /pdf.worker.min.mjs (copied
  // from node_modules at install time; see Phase 10 notes).
  const pdfjs = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf.mjs');
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  pdfjsCache = pdfjs;
  return pdfjs;
}

async function extractPdfText(file) {
  const buf = await file.arrayBuffer();
  const pdfjs = await loadPdfJs();

  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: buf }).promise;
  } catch (e) {
    if (e?.name === 'PasswordException' || /password/i.test(String(e?.message))) {
      throw new Error('encrypted');
    }
    throw e;
  }

  const pieces = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const lineText = content.items
      .map((it) => ('str' in it ? it.str : ''))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (lineText) pieces.push(lineText);
  }

  return pieces.join('\n').trim();
}

/* ================================================================
   Idle view — drag-drop zone
   ================================================================ */

function IdleView({ dragHover, onPick, onPickFile, onDragOver, onDragLeave, onDrop, fileInputRef }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary, lineHeight: 1.5 }}>
        We'll extract your skills automatically. PDF only, max 5MB.
      </p>
      <button
        type="button"
        onClick={onPick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          padding: '40px 16px',
          borderRadius: radius.lg,
          border: `2px dashed ${dragHover ? colors.primary : colors.border}`,
          background: dragHover ? 'rgba(47, 91, 255, 0.06)' : 'var(--bg-surface-2)',
          color: dragHover ? colors.primary : colors.textSecondary,
          fontFamily: fontStack,
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <UploadGlyph color={dragHover ? colors.primary : colors.textSecondary} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Drag PDF here or click to browse</div>
          <div style={{ marginTop: 4, fontSize: 12, color: colors.textSecondary }}>
            We don't store the file — only the extracted text is sent.
          </div>
        </div>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={onPickFile}
        style={{ display: 'none' }}
      />
    </div>
  );
}

/* ================================================================
   Parsing view — progressive status
   ================================================================ */

const STATUS_LINES = [
  'Extracting text from PDF…',
  'Identifying skills…',
  'Almost there…',
];

function ParsingView({ progress }) {
  return (
    <div style={{ padding: '8px 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {STATUS_LINES.map((line, i) => {
        const done = i < progress;
        const active = i === progress;
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 14,
              color: active ? colors.textPrimary : (done ? colors.textSecondary : '#C7CEDB'),
              fontWeight: active ? 600 : 400,
            }}
          >
            <StatusDot state={done ? 'done' : active ? 'active' : 'pending'} />
            <span>{line}</span>
          </div>
        );
      })}
      <style>{`
        @keyframes cv-pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
        .cv-pulse-dot {
          animation: cv-pulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function StatusDot({ state }) {
  if (state === 'done') {
    return (
      <span
        aria-hidden="true"
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: colors.accentEnergy, color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700,
        }}
      >
        ✓
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span
        aria-hidden="true"
        className="cv-pulse-dot"
        style={{
          width: 12, height: 12, borderRadius: '50%',
          background: colors.primary,
        }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        width: 12, height: 12, borderRadius: '50%',
        border: `1.5px solid ${colors.border}`,
      }}
    />
  );
}

/* ================================================================
   Review view
   ================================================================ */

function ReviewView({
  extracted, skills, editingId, setEditingId,
  updateRow, removeRow, addRow, saveError,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary, lineHeight: 1.5 }}>
        Edit anything that's wrong. Remove what doesn't fit.
      </p>

      <div style={{
        fontSize: 13, color: colors.textSecondary, fontStyle: 'italic',
      }}>
        Detected:{' '}
        <strong style={{ color: colors.textPrimary, fontStyle: 'normal' }}>
          {capitalize(extracted.experience_level)}-level
        </strong>{' '}
        •{' '}
        <strong style={{ color: colors.textPrimary, fontStyle: 'normal' }}>
          {extracted.primary_domain}
        </strong>{' '}
        domain
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {skills.map((row) => {
          const isEditing = editingId === row.id;
          return (
            <li
              key={row.id}
              style={{
                padding: '12px 4px',
                borderBottom: `1px solid ${colors.border}`,
                background: row.dirty ? 'rgba(47, 91, 255, 0.04)' : 'transparent',
                borderRadius: radius.sm,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div className="cv-review-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}>
                {isEditing ? (
                  <input
                    type="text"
                    value={row.skill_name}
                    onChange={(e) => updateRow(row.id, { skill_name: e.target.value.slice(0, 60) })}
                    placeholder="Skill name"
                    autoFocus
                    style={{ ...inputStyle, flex: '1 1 160px', minWidth: 0 }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingId(row.id)}
                    style={{
                      flex: '1 1 160px', minWidth: 0,
                      textAlign: 'left',
                      background: 'transparent', border: 'none', cursor: 'text',
                      padding: '8px 4px', font: 'inherit', color: colors.textPrimary,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      fontSize: 14, fontWeight: 600,
                    }}
                  >
                    {row.skill_name || <span style={{ color: 'var(--danger)' }}>Skill name required</span>}
                  </button>
                )}

                <select
                  value={row.category}
                  onChange={(e) => updateRow(row.id, { category: e.target.value })}
                  aria-label={`Category for ${row.skill_name || 'new skill'}`}
                  style={{ ...inputStyle, width: 130 }}
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>

                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="100"
                  step="1"
                  value={row.score}
                  onChange={(e) => updateRow(row.id, { score: e.target.value })}
                  aria-label={`Score for ${row.skill_name || 'new skill'}`}
                  style={{ ...inputStyle, width: 78, textAlign: 'right' }}
                />

                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  aria-label={`Remove ${row.skill_name || 'skill'}`}
                  style={iconBtn}
                >
                  <CloseGlyph />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <button type="button" onClick={addRow} style={addRowBtn}>+ Add another skill</button>

      {saveError && (
        <p style={{ margin: 0, color: 'var(--danger)', fontSize: 13 }}>{saveError}</p>
      )}

      <style>{`
        @media (max-width: 480px) {
          .cv-review-row {
            gap: 6px;
          }
          .cv-review-row select { width: 110px !important; }
          .cv-review-row input[type="number"] { width: 64px !important; }
        }
      `}</style>
    </div>
  );
}

function capitalize(s) {
  if (!s) return '';
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
}

/* ================================================================
   Error view
   ================================================================ */

function ErrorView({ title, message, onRetry, onAddManually }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary, lineHeight: 1.5 }}>
        {message || 'Something went wrong reading this PDF.'}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button type="button" onClick={onRetry} style={primaryBtn(false)}>Try again</button>
        <button
          type="button"
          onClick={onAddManually}
          style={{
            ...ghostBtn,
            background: 'transparent',
            color: colors.primary,
          }}
        >
          Add manually instead
        </button>
      </div>
    </div>
  );
}

/* ================================================================
   Glyphs + styles
   ================================================================ */

function UploadGlyph({ color }) {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
function CloseGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

const closeBtn = {
  width: 32, height: 32, padding: 0,
  border: 'none', background: 'transparent', borderRadius: 999,
  cursor: 'pointer', color: colors.textSecondary, fontSize: 18, fontFamily: fontStack,
};

const primaryBtn = (disabled) => ({
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  border: 'none',
  background: disabled ? '#9CB1FF' : colors.primary,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const ghostBtn = {
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const addRowBtn = {
  alignSelf: 'flex-start',
  padding: '8px 14px',
  minHeight: 36,
  borderRadius: 999,
  border: `1px dashed ${colors.primary}`,
  background: 'transparent',
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const iconBtn = {
  width: 32, height: 32, padding: 0,
  border: 'none', background: 'transparent',
  borderRadius: 8, cursor: 'pointer',
  color: colors.textSecondary,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const inputStyle = {
  padding: '8px 10px',
  minHeight: 36,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 14,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

